import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function EndocrinologyPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d62j4380h9ov63emh8dg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <div className="inline-block px-4 py-2 bg-[#7C3AED]/10 rounded-full text-[#7C3AED] text-sm font-medium mb-6">
                Endocrinologists & Metabolic Health Specialists
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Attract Patients Seeking Expert Hormone & Metabolic Care
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                HIPAA-compliant digital marketing for endocrinologists and metabolic health specialists who want to grow with qualified patients seeking comprehensive hormone optimization.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#contact-form" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap">
                  Book Free Strategy Call
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <Link to="/services/marketing-automation" className="inline-flex items-center px-8 py-4 bg-white text-[#7C3AED] rounded-lg font-semibold hover:bg-[#7C3AED]/5 transition-all border-2 border-[#7C3AED] whitespace-nowrap">
                  See Our Automation Platform
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/2faaaf3c9e6ce0837d261af192675168.jpg"
                alt="Endocrinology Practice"
                className="w-full h-96 object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Marketing Challenges for Endocrinologists
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the unique obstacles facing endocrinology and metabolic health practices
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-search-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Finding Ideal Patients</h3>
              <p className="text-gray-600">
                Attracting patients who need specialized endocrine care beyond basic diabetes management.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-search-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Low Online Visibility</h3>
              <p className="text-gray-600">
                Patients searching for hormone specialists, thyroid experts, or metabolic health doctors can't find your practice.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Referral Dependency</h3>
              <p className="text-gray-600">
                Over-reliance on physician referrals limits growth and makes patient volume unpredictable.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Patient Education Gap</h3>
              <p className="text-gray-600">
                Most patients don't understand when they need an endocrinologist versus their primary care doctor.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-phone-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Missed Inquiries</h3>
              <p className="text-gray-600">
                Patient inquiries go unanswered when you're in clinic, leading to lost appointments and revenue.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-cross-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">HIPAA Compliance</h3>
              <p className="text-gray-600">
                Ensuring all marketing tools and patient communications meet strict healthcare privacy requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our HIPAA-Compliant Marketing Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven strategies designed specifically for endocrinology practices
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20endocrinologist%20doctor%20consulting%20with%20patient%20about%20hormone%20health%20and%20metabolic%20wellness%2C%20modern%20medical%20office%2C%20doctor%20patient%20consultation%2C%20endocrine%20care%2C%20thyroid%20and%20hormone%20specialist%20consultation&width=600&height=400&seq=endo1&orientation=landscape"
                alt="Endocrinologist consultation"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Automated Patient Acquisition System
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Our marketing automation platform captures, educates, and converts patients while you focus on clinical care.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">Instant Response:</strong> Automated follow-up within minutes of every inquiry</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">Patient Education:</strong> Automated content explaining when to see an endocrinologist</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">Appointment Automation:</strong> Seamless booking that fills your schedule</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">HIPAA-Conscious:</strong> Secure systems designed for healthcare compliance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Marketing Services for Your Practice
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to attract and convert more patients
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link to="/services/medical-seo" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Medical SEO</h3>
              <p className="text-gray-600 mb-4">
                Rank #1 for "endocrinologist near me," "thyroid specialist," and hormone health searches in your area.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/medical-website-design" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-layout-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Website Design</h3>
              <p className="text-gray-600 mb-4">
                Professional websites that educate patients about endocrine conditions and drive appointment bookings.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/content-marketing" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-article-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Content Marketing</h3>
              <p className="text-gray-600 mb-4">
                Educational content about thyroid, hormones, and metabolic health that establishes your authority.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/healthcare-ppc" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-advertisement-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Google & Meta Ads</h3>
              <p className="text-gray-600 mb-4">
                HIPAA-compliant paid advertising targeting patients actively seeking endocrine care.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/marketing-automation" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-robot-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Marketing Automation</h3>
              <p className="text-gray-600 mb-4">
                Never miss a lead with automated nurturing, follow-up, and patient engagement systems.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/reputation-management" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Reputation Management</h3>
              <p className="text-gray-600 mb-4">
                Build trust with more 5-star reviews from satisfied patients across healthcare directories.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Leading Endocrinologists
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-8 rounded-xl border border-purple-100">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20female%20endocrinologist%20doctor%20headshot%20portrait%2C%20hormone%20specialist%2C%20confident%20medical%20professional%2C%20thyroid%20and%20metabolic%20health%20expert&width=80&height=80&seq=endotest1&orientation=squarish"
                  alt="Dr. Susan Park"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <div className="font-bold text-gray-900">Dr. Susan Park, MD</div>
                  <div className="text-sm text-gray-600">Endocrinology & Metabolic Health</div>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed">
                "MillionDollarMed transformed our patient acquisition. Their automation platform ensures we never miss an inquiry, and we've reduced our referral dependency by 60%. New patient volume has increased 200% in 10 months. Outstanding results!"
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-8 rounded-xl border border-purple-100">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20male%20endocrinologist%20doctor%20headshot%20portrait%2C%20thyroid%20specialist%2C%20experienced%20hormone%20health%20physician%2C%20confident%20healthcare%20provider&width=80&height=80&seq=endotest2&orientation=squarish"
                  alt="Dr. David Kumar"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <div className="font-bold text-gray-900">Dr. David Kumar, MD</div>
                  <div className="text-sm text-gray-600">Thyroid & Hormone Specialist</div>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed">
                "Finally found a marketing partner who understands endocrinology. Their SEO strategy has us ranking #1 for thyroid specialist in our city, and the automated follow-up converts leads while I'm seeing patients. Highly recommend!"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Free Strategy Call
            </h2>
            <p className="text-xl text-gray-600">
              Let's discuss how we can help grow your endocrinology practice with HIPAA-compliant marketing and automation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-2xl shadow-lg" data-readdy-form id="endocrinology-strategy-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Dr. Jane Smith"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="doctor@practice.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label htmlFor="practice" className="block text-sm font-semibold text-gray-700 mb-2">
                  Practice Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="practice"
                  name="practice"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Your Practice Name"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="specialty" className="block text-sm font-semibold text-gray-700 mb-2">
                Specialty Focus <span className="text-red-500">*</span>
              </label>
              <select
                id="specialty"
                name="specialty"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              >
                <option value="">Select your specialty</option>
                <option value="endocrinology">Endocrinology</option>
                <option value="thyroid-specialist">Thyroid Specialist</option>
                <option value="metabolic-health">Metabolic Health</option>
                <option value="diabetes-endocrinology">Diabetes & Endocrinology</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                What are your biggest marketing challenges? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                placeholder="Tell us about your practice goals and challenges..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
            </div>

            <div className="mb-6">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="consent"
                  required
                  className="mt-1 mr-3"
                />
                <span className="text-sm text-gray-600">
                  I agree to receive communications from MillionDollarMed. All information is handled in accordance with HIPAA guidelines.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-violet-600 text-white py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-violet-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book My Free Strategy Call
            </button>

            {formStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-center font-semibold">
                  Thank you! We'll contact you within 24 hours to schedule your strategy call.
                </p>
              </div>
            )}

            {formStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-center font-semibold">
                  Something went wrong. Please try again or call us directly.
                </p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Endocrinology Practice?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join leading endocrinologists who trust MillionDollarMed for HIPAA-compliant marketing and automated patient acquisition.
          </p>
          <a href="#contact-form" className="inline-flex items-center px-8 py-4 bg-white text-[#0D9488] rounded-lg font-semibold hover:bg-gray-50 transition-all text-lg whitespace-nowrap">
            Schedule Your Free Consultation
            <i className="ri-calendar-line ml-2"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
